import './ShortlistedCandidates.scss';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchList } from 'services/common/react-query-common-service';
import { SALES_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../../components/StylishLoader';

const NoMatches = () => (
  <Box className="no-candidates">
    <Box className="icon-box">
      <svg
        width="36"
        height="36"
        viewBox="0 0 36 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14 3C7.92487 3 3 7.92487 3 14C3 20.0751 7.92487 25 14 25C20.0751 25 25 20.0751 25 14C25 7.92487 20.0751 3 14 3ZM0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 17.3257 26.8404 20.3805 24.9035 22.7822L35.5607 33.4393C36.1464 34.0251 36.1464 34.9749 35.5607 35.5607C34.9749 36.1464 34.0251 36.1464 33.4393 35.5607L22.7822 24.9035C20.3805 26.8404 17.3257 28 14 28C6.26801 28 0 21.732 0 14ZM14 12C14.8284 12 15.5 12.6716 15.5 13.5V20.5C15.5 21.3284 14.8284 22 14 22C13.1716 22 12.5 21.3284 12.5 20.5V13.5C12.5 12.6716 13.1716 12 14 12ZM14 10C15.1046 10 16 9.10457 16 8C16 6.89543 15.1046 6 14 6C12.8954 6 12 6.89543 12 8C12 9.10457 12.8954 10 14 10Z"
          fill="currentColor"
        />
      </svg>
    </Box>
    <Typography className="title">No Approved candidates yet</Typography>
    <Typography className="subtitle">
      Candidates will appear here after you approved them for client processing.
      Review candidates in the “Ready for Review” tab and Approved to move them
      here.
    </Typography>
  </Box>
);

interface ApprovedCandidatesProps {
  jobId?: string;
  tableType?: string;
  score?: string;
}

const ApprovedCandidates: React.FC<ApprovedCandidatesProps> = ({
  jobId,
  score,
  tableType,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateTo = (item: any, jobId: string) => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : '/business-head';
    const candidateId = item?.id || '';

    navigate(`${basePath}/view-candidate/${String(candidateId)}`, {
      state: {
        Params: {
          candidateInfo: item,
          jobId: jobId,
          score,
          tableType,
        },
      },
    });
  };

  const { data, isLoading } = useFetchList<any>(
    ['approved-candidates', jobId, score],
    `${String(SALES_ENDPOINT.MatchedCandidates)}/${String(
      jobId,
    )}?type=approved&sourceQueue=jobQueue&score=${score}`,
    {
      enabled: !!jobId,
      refetchOnMount: 'always',
    },
  );

  if (isLoading) {
    return (
      <Box className="loader-container">
        <StylishLoader mode={'table'} text="Loading candidates..." />
      </Box>
    );
  }

  const candidates = data?.data || [];

  if (candidates.length === 0) {
    return (
      <Box className="shortlisted-candidates-container">
        <NoMatches />
      </Box>
    );
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Box className="shortlisted-candidates-container">
      {candidates.map((item: any) => {
        const c = item.candidate;
        const firstName = String(c?.firstName || '');
        const middleName = String(c?.middleName || '');
        const lastName = String(c?.lastName || '');
        const name = `${firstName} ${middleName} ${lastName}`.trim() || '-';

        const matchPercentage = item?.matchPercentage || 0;
        const match = `${String(matchPercentage)}%`;
        const visa = c?.workStatus?.name || '';
        const source = c?.source || '';

        const availability = c?.benchStatus || '';
        const availabilityDate = c?.benchAvailability || '';

        const locationStr =
          [c?.city, c?.state, c?.country].filter(Boolean).join(', ') || '';
        const phone = c?.phone || c?.mobileNumber || '';
        const email = c?.email || '';

        const contactLine = [email, phone, locationStr].filter(Boolean);

        const submittedBy = item?.submittedForReviewUser
          ? `${String(item.submittedForReviewUser.firstName)} ${String(
              item.submittedForReviewUser.lastName,
            )}`
          : '-';
        const submittedOn = formatDate(item?.submittedForReviewUser?.createdAt);

        const cardId = String(item.id);

        return (
          <Box key={cardId} className="candidate-card">
            <Box className="card-content">
              <Box className="top-row">
                <Box className="badges-group">
                  {match && <Box className="badge match-score">{match}</Box>}
                  {visa && <Box className="badge work-status">{visa}</Box>}
                  {source && <Box className="badge source-badge">{source}</Box>}
                </Box>
              </Box>

              <Box className="info-body">
                <Box className="details-wrapper">
                  <Box className="main-info-row">
                    <Box className="name-section">
                      <span
                        className="name"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateTo(item, jobId || '');
                        }}
                        style={{ cursor: 'pointer' }}>
                        {name}
                      </span>
                      {availability && (
                        <span className="availability">
                          <CheckCircleIcon className="check-icon" />
                          {availability}
                          <span className="dot">•</span>
                          {availabilityDate}
                        </span>
                      )}
                    </Box>

                    <Box className="submission-info">
                      <Box className="info-block">
                        <Typography className="label">Submitted by</Typography>
                        <Typography className="value">{submittedBy}</Typography>
                      </Box>
                      <Box className="info-block">
                        <Typography className="label">Submitted on</Typography>
                        <Typography className="value">{submittedOn}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box className="contact-row">
                    {contactLine.map((text, idx) => (
                      <React.Fragment key={idx}>
                        <span>{text}</span>
                        {idx < contactLine.length - 1 && (
                          <span className="contact-dot">•</span>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ApprovedCandidates;
